import { api } from "@/services/api";

/**
 * Sesión y auth del Socio · cookie HttpOnly.
 *
 * Fuente de verdad: backend. La cookie `access_token` la emite y borra
 * el servidor (`/api/socios/auth/login|logout`); el navegador la envía
 * automáticamente en cada request. En `localStorage` sólo se guarda un
 * snapshot no sensible del socio (`ct_current_socio_v1`) para reads
 * síncronos de la UI.
 */

const KEY_SOCIO = "ct_current_socio_v1";

const isBrowser = () =>
  typeof window !== "undefined" && typeof window.localStorage !== "undefined";

const readJson = (key, fallback) => {
  if (!isBrowser()) return fallback;
  try {
    const raw = window.localStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
};

const writeJson = (key, value) => {
  if (!isBrowser()) return;
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* ignore */
  }
};

const removeKey = (key) => {
  if (!isBrowser()) return;
  try {
    window.localStorage.removeItem(key);
  } catch {
    /* ignore */
  }
};

/**
 * Devuelve la sesión activa del socio. Nunca inventa datos: si no hay
 * sesión, devuelve `null`. La validez real de la sesión la determina
 * el backend rechazando requests con cookie ausente/expirada.
 */
export const getCurrentSocio = () => {
  const s = readJson(KEY_SOCIO, null);
  if (!s || !s.id) return null;
  return s;
};

export const isSocioAuthenticated = () => getCurrentSocio() !== null;

/**
 * Login real contra `/api/socios/auth/login`. La cookie de sesión se
 * setea automáticamente en la respuesta. Devuelve el snapshot del
 * socio o `null` si falla la autenticación.
 */
export const loginSocio = async (numeroSocio, password) => {
  const num = String(numeroSocio || "").trim();
  const pass = String(password || "");
  if (!num || !pass) return null;
  try {
    const { data } = await api.post("/socios/auth/login", {
      numeroSocio: num,
      password: pass,
    });
    if (!data || !data.socio) return null;
    const snapshot = {
      id: data.socio.numeroSocio,
      numeroSocio: data.socio.numeroSocio,
      nombre: data.socio.nombre,
      correo: data.socio.correo || "",
      tipoSocio: data.socio.tipoSocio,
      membresiaId: data.socio.membresiaId,
      membresiaBloqueada: !!data.socio.membresiaBloqueada,
      estatus: data.socio.estatus,
    };
    writeJson(KEY_SOCIO, snapshot);
    return snapshot;
  } catch (err) {
    const status = err?.response?.status;
    if (status === 401 || status === 403) return null;
    return { error: "network" };
  }
};

export const logoutSocio = () => {
  removeKey(KEY_SOCIO);
  api.post("/socios/auth/logout").catch(() => {});
};

/**
 * Trae el snapshot actualizado del socio autenticado desde el backend
 * (`GET /api/socios/auth/me`). Se usa cuando la UI necesita la
 * identidad "fresca" (por ejemplo el botón "Usar mi nombre" en la
 * asignación de asistentes: no queremos depender exclusivamente del
 * snapshot local). Devuelve `null` si no hay sesión válida.
 */
export const fetchSocioMe = async () => {
  try {
    const { data } = await api.get("/socios/auth/me");
    if (!data || !data.numeroSocio) return null;
    const snapshot = {
      id: data.numeroSocio,
      numeroSocio: data.numeroSocio,
      nombre: data.nombre,
      correo: data.correo || "",
      tipoSocio: data.tipoSocio,
      membresiaId: data.membresiaId,
      membresiaBloqueada: !!data.membresiaBloqueada,
      estatus: data.estatus,
    };
    writeJson(KEY_SOCIO, snapshot);
    return snapshot;
  } catch {
    return null;
  }
};

// Sprint 47 · limpieza local sin llamar backend (usada por el listener
// global de sesión terminada por el servidor).
export const clearSocioSessionLocal = () => removeKey(KEY_SOCIO);
